<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, helpers, email } from '@vuelidate/validators'
const { t, locale } = useI18n()
const localePath = useLocalePath()
const toast = useToast()
const url = useRuntimeConfig().public.baseURL

// In smsbat.com, users are not logged in natively. We only use the non-auth path.
const isFetching = ref(false)

type NewBC = {
  commercialAccountId: number | null
  businessName: string
  businessNameEnglish: string
  countryCode: { id: number; name: string; code: string } | null | number
  countryId: null | number
  numberRequested: boolean
  expectedMonthlyVolume: number | null
  desiredPhoneFormat: string
  companyName: string
  phoneNumber: string
  email: string
  referralGuid: string | null
  businessDescription: string
}

const newBC = ref<NewBC>({
  commercialAccountId: null,
  businessName: '',
  businessNameEnglish: '',
  countryCode: null,
  countryId: null,
  numberRequested: false,
  expectedMonthlyVolume: null,
  desiredPhoneFormat: '',
  companyName: '',
  phoneNumber: '',
  email: '',
  referralGuid: null,
  businessDescription: ''
})

const rulesForNonAuth = {
  commercialAccountId: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  businessName: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  businessNameEnglish: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  numberRequested: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  expectedMonthlyVolume: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  desiredPhoneFormat: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  companyName: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  },
  phoneNumber: {
    required: helpers.withMessage(() => t('validation.reqField'), required),
  },
  email: {
    required: helpers.withMessage(() => t('validation.emailReq'), required),
    email: helpers.withMessage(() => t('validation.emailValid'), email)
  },
  countryId: {
    required: helpers.withMessage(() => t('validation.reqField'), required)
  }
}

const v$ForNonAuth = useVuelidate(rulesForNonAuth, newBC)

const resetNewBC = () => {
  newBC.value = {
    commercialAccountId: null,
    businessName: '',
    businessNameEnglish: '',
    countryCode: null,
    countryId: null,
    numberRequested: false,
    expectedMonthlyVolume: null,
    desiredPhoneFormat: '',
    companyName: '',
    phoneNumber: '',
    email: '',
    referralGuid: null,
    businessDescription: ''
  }
}

function showToast(severity: "success" | "info" | "warn" | "error" | "secondary" | "contrast" | undefined, detail: string) {
  toast.add({ severity, summary: t(`statuses.${severity}`), detail, life: 3000 });
}

const sendRequestToIndexbat = async () => {
  const res = await v$ForNonAuth.value.$validate()
  if (res) {
    const formD = new FormData();
    formD.append('CommercialAccountId', String(newBC.value.commercialAccountId))
    formD.append('BusinessName', newBC.value.businessName)
    formD.append('BusinessNameEnglish', newBC.value.businessNameEnglish)
    formD.append('IsNumberRequested', String(newBC.value.numberRequested))
    formD.append('ExpectedMonthlyVolume', String(newBC.value.expectedMonthlyVolume))
    formD.append('DesiredPhoneFormat', newBC.value.desiredPhoneFormat)
    formD.append("ContactInfo.CompanyName", newBC.value.companyName);
    formD.append("CountryId", String(newBC.value.countryId));
    formD.append("ContactInfo.PhoneNumber", newBC.value.phoneNumber);
    formD.append("ContactInfo.Email", newBC.value.email);

    if (newBC.value.businessDescription) {
      formD.append('BusinessDescription', newBC.value.businessDescription)
    }
    if (newBC.value.referralGuid) {
      formD.append('ReferralGuid', newBC.value.referralGuid)
    }
    try {
      isFetching.value = true

      await $fetch(`${url}/viber-bc/anonymous`, {
        method: "POST",
        body: formD,
        onResponse: ({ response }) => {
          if (response.status == 200) {
            toast.add({
              severity: "success",
              summary: t('statuses.success'),
              detail: t('notifications.success.applicationWasCreated'),
              life: 3000,
            });
            showPaymentModal.value = true;
          }
        },
        onResponseError: ({ response }) => {
          if (response._data?.errors?.ReferralGuid) {
             showToast("error", t('notifications.error.unvalidReferralGuid'))
          } else if (response._data?.errors?.length > 0) {
            const errors = response._data?.errors;
            if (errors.some((error: string) => error.includes("Referral not found"))) {
               showToast("error", t("notifications.error.referralNotFound"))
            }
          }
        }
      });
      // Delay reset depending on Modal logic, wait until action
    } catch (error) {
       console.error('Failed to create BC application', error);
    } finally {
      isFetching.value = false
    }
  }
};

const payWithWhop = () => {
  window.open('https://whop.com/checkout/plan_KVD6Ju0h9zQ0d', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewBC();
  v$ForNonAuth.value.$reset();
};

const payWithSwift = () => {
  window.open('viber://chat?service=26076', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewBC();
  v$ForNonAuth.value.$reset();
};

const showPaymentModal = ref(false);

const submitForm = async () => {
    const isValid = await v$ForNonAuth.value.$validate()
    if (!isValid) return
    await sendRequestToIndexbat()
}

const countryList = ref([]);
watch(locale, async () => {
  const urlLang = locale.value === 'uk-ua' ? 'uk' : 'en'
  const url = `https://widget.smsbat.com/translates/countries.${urlLang}.json`
  const res = await fetch(url);
  countryList.value = await res.json();
}, { immediate: true })

onMounted(() => {
  // Always true for landing page
  haveViberAccount.value = true
})

const haveViberAccount = ref(true)
</script>

<template>
  <form class="flex flex-col gap-4 w-full" @submit.prevent="submitForm">
    <!-- Payment Link Modal -->
    <Dialog v-model:visible="showPaymentModal" :header="t('registerBotLanding.form.paymentRequired')" :modal="true" :dismissableMask="false" :closable="false" style="width: 50vw; max-width: 600px;">
      <div class="flex flex-col gap-6 pt-4 text-center">
        <i class="pi pi-check-circle text-green-500 mx-auto" style="font-size: 3rem;"></i>
        <h3 class="text-xl font-bold text-gray-800">{{ t('registerBotLanding.form.applicationSuccess') }}</h3>
        
        <div class="bg-blue-50 p-6 rounded-xl border border-blue-100 flex flex-col gap-3">
          <p class="text-blue-900 font-semibold">{{ t('registerBotLanding.form.paymentNextSteps') }}</p>
          <div class="flex items-center justify-between mt-2 pt-2 border-t border-blue-200">
            <span class="text-blue-800">{{ t('bc.pricing.total.title') }}:</span>
            <span class="text-2xl font-bold text-[#3070b6]">390 EUR</span>
          </div>
          <p class="text-sm text-blue-600 font-medium">{{ t('bc.pricing.total.breakdown') }}</p>
          <p class="text-xs text-blue-500 italic mt-2">{{ t('registerBotLanding.pricing.vatNote') }}</p>
        </div>

        <div class="flex flex-col sm:flex-row gap-4 mt-4 justify-center">
            <Button :label="t('registerBotLanding.form.payWithCard')" icon="pi pi-credit-card" @click="payWithWhop" class="w-full sm:w-auto p-button-lg" style="background-color: #3070b6; border-color: #3070b6" />
            <Button :label="t('registerBotLanding.form.payByInvoice')" icon="pi pi-file-invoice" severity="secondary" @click="payWithSwift" class="w-full sm:w-auto p-button-lg p-button-outlined" />
        </div>
      </div>
    </Dialog>

    <div v-if="haveViberAccount">
      <InputNumber v-model="newBC.commercialAccountId as any" :useGrouping="false" :placeholder="$t('bcReg.caId')" class="w-full no-spinners"
        v-keyfilter.int :maxlength="16" />
      <small v-for="error in v$ForNonAuth.commercialAccountId.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message
      }}</small>
    </div>

    <div v-if="haveViberAccount">
      <InputText v-model="newBC.businessName" :placeholder="$t('bcReg.businessName')" class="w-full" />
      <small v-for="error in v$ForNonAuth.businessName.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message
      }}</small>
    </div>

    <div v-if="haveViberAccount">
      <InputText v-model="newBC.businessNameEnglish" :placeholder="$t('bcReg.businessNameEnglish')" class="w-full" />
      <small v-for="error in v$ForNonAuth.businessNameEnglish.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message
      }}</small>
    </div>

    <div>
      <Select v-model="newBC.countryId" :options="countryList" option-label="name" option-value="id"
        :placeholder="$t('global.country')" filter class="w-full" />
      <small v-for="error in v$ForNonAuth.countryId.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message
      }}</small>
    </div>

    <div class="flex items-center gap-4">
      <InputSwitch v-model="newBC.numberRequested" />
      <span>{{ $t('bcReg.isNumberRequested') }}</span>
    </div>

    <div>
      <InputText v-model="newBC.businessDescription" :placeholder="$t('bcReg.businessDesc')" class="w-full" />
    </div>

    <div>
      <InputNumber v-model="newBC.expectedMonthlyVolume as any" :useGrouping="false" :placeholder="$t('bcReg.expectedMonthlyVolume')"
        class="w-full  no-spinners" v-keyfilter.int :maxlength="9" />
      <small v-for="error in v$ForNonAuth.expectedMonthlyVolume.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message
      }}</small>
    </div>

    <div>
      <InputText v-model="newBC.desiredPhoneFormat" :placeholder="$t('bcReg.numberFormat')" class="w-full" />
      <small v-for="error in v$ForNonAuth.desiredPhoneFormat.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message
      }}</small>
    </div>

    <div>
      <InputText v-model="newBC.companyName" :placeholder="$t('bcReg.companyName')" class="w-full" />
      <small v-for="error in v$ForNonAuth.companyName.$errors" class="text-red-500 -mt-4 text-xs">{{
        error.$message }}</small>
    </div>

    <div>
      <InputText v-model="newBC.phoneNumber" :placeholder="$t('global.phoneNumber')" class="w-full no-spinners"
        v-keyfilter.int :maxlength="16" />
      <small v-for="error in v$ForNonAuth.phoneNumber.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message
        }}</small>
    </div>

    <div>
      <InputText v-model="newBC.email" :placeholder="$t('global.email')" class="w-full" />
      <small v-for="error in v$ForNonAuth.email.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>
    </div>
    
    <div>
      <InputText v-model="newBC.referralGuid" :placeholder="$t('global.referralGuid')" class="w-full" />
    </div>

    <Button type="submit" :label="$t('bcReg.registerSIP')" :disabled="isFetching" />
  </form>
</template>